/**
 * Employee edit — fed from `$employeeId.index`'s "Edit" CTA.
 *
 * Fetches with `reveal: true` so Aadhaar + bank account prefill with full
 * values (the audited reveal path). Posts master-field diffs only.
 *
 * @see prototype/payroll/employee-edit.html
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import {
  EmployeeForm,
  formFromEmployee,
  valuesToUpdatePayload,
} from "../../../../features/employees/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";

export const Route = createFileRoute("/(workspace)/payroll/employees/$employeeId/edit")({
  component: EditEmployeePage,
});

function EditEmployeePage() {
  const { employeeId } = Route.useParams();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  // reveal: true → full Aadhaar + bank account for prefill (audited).
  const employee = trpc.employees.get.useQuery({ id: employeeId, reveal: true });

  const update = trpc.employees.update.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.employees.list.invalidate(),
        utils.employees.get.invalidate({ id: employeeId }),
      ]);
    },
  });

  const baseCrumbs = [
    { label: "Payroll", to: "/payroll/employees" },
    { label: "Employees", to: "/payroll/employees" },
  ];

  if (employee.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Edit" }]}>
        <p className="text-muted">Loading employee…</p>
      </WorkspaceShell>
    );
  }
  if (employee.error) {
    return (
      <WorkspaceShell breadcrumbs={[...baseCrumbs, { label: "Edit" }]}>
        <Alert intent="bad" title="Couldn't load employee" description={employee.error.message} />
      </WorkspaceShell>
    );
  }
  if (!employee.data) return null;

  const baseline = formFromEmployee(employee.data);

  return (
    <WorkspaceShell
      breadcrumbs={[
        ...baseCrumbs,
        { label: employee.data.name, to: "/payroll/employees/$employeeId" },
        { label: "Edit" },
      ]}
      contentClassName="max-w-none h-full"
      viewportClassName="p-0"
      disableViewportScroll
    >
      <EmployeeForm
        defaultValues={baseline}
        submitLabel="Save changes"
        pendingLabel="Saving…"
        pending={update.isPending}
        serverError={update.error?.message ?? null}
        mode="edit"
        backHref={`/payroll/employees/${employeeId}`}
        employeeId={employeeId}
        photoUrl={employee.data.photoUrl}
        onSubmit={async (values) => {
          const patch = valuesToUpdatePayload(values, baseline, employeeId);
          const changedKeys = Object.keys(patch).filter((k) => k !== "id");
          if (changedKeys.length > 0) {
            await update.mutateAsync(patch);
          }
          void navigate({ to: "/payroll/employees/$employeeId", params: { employeeId } });
        }}
      />
    </WorkspaceShell>
  );
}
